import { selectIsInternalAdmin } from 'accessors'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import DataIsReprocessingBar from 'components/ReusableComponents/DataIsReprocessingBar'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import InvoiceIcon from 'components/ReusableComponents/Icons/Invoice'
import PlayIcon from 'components/ReusableComponents/Icons/Play'
import InvoiceRecipientsDialog from 'components/ReusableComponents/InvoiceRecipientsDialog'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Toggle from 'components/ReusableComponents/Toggle'
import YouTubeDialog from 'components/ReusableComponents/YouTubeDialog'
import GroupLevelControls from 'features/UserPermissions/GroupLevelControls'
import UserInfo from 'features/UserPermissions/UserInfo'
import UserInfoColumn from 'features/UserPermissions/UserInfoColumn'
import Column from 'features/UserPermissions/UserPermissionColumn'
import SubColumn from 'features/UserPermissions/UserPermissionSubColumn'
import usePlausible from 'hooks/usePlausible'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import userApi from 'services/api/user'
import coreApi from 'services/coreApi'

import ManageUserDialog from '../ManageUserDialog'
import styles from './UserPermissions.module.scss'

export const userRoleLevels = {
  HasPermission: 'HasPermission',
  HasGroupLevelPermission: 'HasGroupLevelPermission',
  NoPermission: 'NoPermission'
}

export const userRoles = {
  UserManage: 'UserManage',
  Billing: 'Billing',
  BookingsView: 'BookingsView',
  BookingsManage: 'BookingsManage',
  CampaignView: 'CampaignView',
  CampaignManage: 'CampaignManage',
  ReportsView: 'ReportsView',
  ChatInProperty: 'ChatInProperty',
  ChatSalesBefore: 'ChatSalesBefore',
  ChatSalesAfter: 'ChatSalesAfter',
  ChatDirect: 'ChatDirect'
}

const UserPermissions = ({ showInvoiceControls }) => {
  const [users, setUsers] = useState()

  const [showAdmins, setShowAdmins] = useState(false)

  const [isYouTubeVideoVisible, setIsYouTubeVideoVisible] = useState(false)

  const history = useHistory()

  async function getUserRoles() {
    userApi.getList().then(data => {
      setUsers(data.users)
    })
  }

  const [showingGroupLevelControlFor, setShowingGroupLevelControlFor] = useState()

  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  const userManageRole = useSelector(s => s.login.roles.UserManage)

  const canEditUsers =
    [userRoleLevels.HasPermission, userRoleLevels.HasGroupLevelPermission].includes(
      userManageRole
    ) || isInternalAdmin

  useEffect(() => {
    getUserRoles()
  }, [])

  const { addToast } = useToasts()

  // add/edit user

  const [showingAddUserDialog, setShowingAddUserDialog] = useState()
  const [showingEditUserDialog, setShowingEditUserDialog] = useState()

  const [userToBeEditedId, setUserToBeEditedId] = useState()

  async function handleUserEditClick(userId) {
    setUserToBeEditedId(userId)
    setShowingEditUserDialog(true)
  }

  async function handleAddedUserSuccess() {
    setShowingAddUserDialog(false)
    setShowingEditUserDialog(false)

    getUserRoles()
  }

  function handleCloseInvoiceRecipientsDialog() {
    history.push('/userPermissions')
    getUserRoles()
  }

  // delete user

  const [userToBeDeletedId, setUserToBeDeletedId] = useState()
  const [userToBeDeletedName, setUserToBeDeletedName] = useState()
  const [deletingUser, setDeletingUser] = useState()

  async function deleteUser() {
    userApi
      .remove(userToBeDeletedId)
      .then(() => {
        setUsers()
        setDeletingUser(false)
        getUserRoles()
      })
      .catch(() => {
        setDeletingUser(false)
        this.showErrorDialog(
          'Error while deleting user',
          `Something went wrong while deleting ${userToBeDeletedName}. Please try again later.`
        )
      })
  }

  function showUserDeleteConfirmation(name, id) {
    setDeletingUser(true)
    setUserToBeDeletedName(name)
    setUserToBeDeletedId(id)
  }

  // update

  const [saveSaved, setSaveSaved] = useState(false)
  const [saveDisabled, setSaveDisabled] = useState(true)

  const { postEvent, plausibleEvents } = usePlausible()

  function getSingleRole(roleName) {
    return users
      .filter(o => (isInternalAdmin ? true : !o.isInternalGlobalUser))
      .filter(o => (o.isInternalGlobalUser ? showAdmins : true))
      .map(u => ({
        ...u.roles.find(r => r.role === roleName),
        hasCampaignsManageAccess: [
          userRoleLevels.HasGroupLevelPermission,
          userRoleLevels.HasPermission
        ].includes(u.roles.find(r => r.role === 'CampaignManage').accessLevel),
        userId: u.userId,
        name: u.name,
        isInternalGlobalUser: u.isInternalGlobalUser
      }))
      .sort((a, b) => a.userId.localeCompare(b.userId))
  }

  function updateRole(userId, role, accessLevel) {
    const changedUser = users.find(u => u.userId === userId)

    let updatedUsers = [
      ...users.filter(u => u.userId !== userId),
      {
        ...changedUser,
        roles: [...changedUser.roles.filter(r => r.role !== role), { role, accessLevel }]
      }
    ]

    setUsers(updatedUsers)
    setSaveDisabled(false)
  }

  async function saveRoles() {
    postEvent(plausibleEvents.UpdateUserPermissions)

    await userApi.updateRoles({ users })

    setSaveDisabled(true)
    setSaveSaved(true)
  }

  // upgrade/downgrade

  const [showingUpgradeDialog, setShowingUpgradeDialog] = useState(false)

  const [showingDowngradeDialog, setShowingDowngradeDialog] = useState(false)

  const [potentialChange, setPotentialChange] = useState({})

  function handleGroupLevelChange(userId, name, role, accessLevel) {
    setPotentialChange({
      userId,
      name,
      role,
      accessLevel
    })

    if (accessLevel === userRoleLevels.HasGroupLevelPermission) {
      setShowingUpgradeDialog(true)
    }

    if (accessLevel === userRoleLevels.HasPermission) {
      setShowingDowngradeDialog(true)
    }
  }

  function sendDownloadLink(phone) {
    coreApi.sendDownloadLink(phone).then(data => {
      if (data.ok) {
        addToast(`Sent app link to ${phone}`, {
          appearance: 'success',
          autoDismiss: true
        })
      } else {
        addToast(`Couldn't send app link to ${phone}`, {
          appearance: 'error',
          autoDismiss: true
        })
      }
    })
  }

  if (!users) return <LoadingIndicator />

  return (
    <div className="userPermissions">
      <DataIsReprocessingBar />
      {isYouTubeVideoVisible && (
        <YouTubeDialog
          onClose={() => setIsYouTubeVideoVisible(false)}
          videoId="BXgDCaClbOI"
        />
      )}
      {showingEditUserDialog && (
        <ManageUserDialog
          userToEdit={users.find(user => user.userId === userToBeEditedId)}
          handleClose={() => setShowingEditUserDialog(false)}
          handleAddedUser={handleAddedUserSuccess}
        />
      )}
      {showingAddUserDialog && (
        <ManageUserDialog
          handleClose={() => setShowingAddUserDialog(false)}
          handleAddedUser={handleAddedUserSuccess}
        />
      )}
      {showInvoiceControls && (
        <InvoiceRecipientsDialog
          users={users}
          onAddUserClick={() => setShowingAddUserDialog(true)}
          onClose={() => handleCloseInvoiceRecipientsDialog()}
          onSave={() => handleCloseInvoiceRecipientsDialog()}
        />
      )}
      {deletingUser && (
        <ConfirmationDialog
          title="Delete user"
          positiveMessage="Yes, delete"
          negativeMessage="No, cancel"
          onConfirm={deleteUser}
          onClose={() => setDeletingUser(false)}
          hasDangerousAction
        >
          <p>
            Are you sure you want to delete <b>{userToBeDeletedName}</b>?
          </p>
        </ConfirmationDialog>
      )}
      {showingUpgradeDialog && (
        <ConfirmationDialog
          title={`Should upgrade ${potentialChange.name}?`}
          negativeMessage="Cancel"
          positiveMessage="Yes, upgrade"
          onReject={() => {
            setShowingUpgradeDialog(false)
            setPotentialChange({})
          }}
          onConfirm={() => {
            const { userId, role, accessLevel } = potentialChange

            updateRole(userId, role, accessLevel)

            setShowingUpgradeDialog(false)
            setPotentialChange({})
          }}
        >
          <p>This action will upgrade {potentialChange.name} to group level.</p>
        </ConfirmationDialog>
      )}
      {showingDowngradeDialog && (
        <ConfirmationDialog
          title={`Should downgrade ${potentialChange.name}?`}
          negativeMessage="Cancel"
          positiveMessage="Yes, downgrade"
          hasDangerousAction
          onReject={() => {
            setShowingDowngradeDialog(false)
            setPotentialChange({})
          }}
          onConfirm={() => {
            const { userId, role, accessLevel } = potentialChange

            updateRole(userId, role, accessLevel)

            setShowingDowngradeDialog(false)
            setPotentialChange({})
          }}
        >
          <p>This action will downgrade {potentialChange.name} to hotel level.</p>
        </ConfirmationDialog>
      )}
      {showingGroupLevelControlFor && (
        <GroupLevelControls
          userId={showingGroupLevelControlFor}
          onClose={() => setShowingGroupLevelControlFor(null)}
        />
      )}
      <CustomHeader title="User management" width={1080} />
      <div className="userPermissions__content">
        <UserInfoColumn>
          {users
            .filter(o => (isInternalAdmin ? true : !o.isInternalGlobalUser))
            .filter(o => (o.isInternalGlobalUser ? showAdmins : true))
            .sort((a, b) => a.userId.localeCompare(b.userId))
            .map(user => (
              <UserInfo
                name={user.name}
                isOwner={user.role === 'Owner' && true}
                email={user.email}
                phone={user.phone}
                position={user.position}
                key={user.userId}
                id={user.userId}
                isInvoiceRecipient={user.isInvoiceRecipient}
                sendDownloadLink={() => sendDownloadLink(user.phone)}
                handleDeleteClick={() =>
                  showUserDeleteConfirmation(user.name, user.userId)
                }
                handleEditClick={handleUserEditClick}
                onClickManageInGroup={() => setShowingGroupLevelControlFor(user.userId)}
              />
            ))}
          <div className="userPermissions__add-user">
            <button onClick={() => setShowingAddUserDialog(true)}>Add user</button>
          </div>
          {isInternalAdmin && (
            <Toggle
              checked={showAdmins}
              mini
              className={styles.adminToggle}
              labelClass={styles.toggleLabel}
              handleChange={e => setShowAdmins(e.target.checked)}
              label="Show admins"
            />
          )}
        </UserInfoColumn>
        <Column category="Guest messaging">
          <SubColumn
            title="During stay"
            userPermissions={getSingleRole(userRoles.ChatInProperty)}
            updateRole={updateRole}
          />
          {isInternalAdmin && (
            <SubColumn
              title="Before stay"
              userPermissions={getSingleRole(userRoles.ChatSalesBefore)}
              updateRole={updateRole}
            />
          )}
          <SubColumn
            title="Reviews"
            userPermissions={getSingleRole(userRoles.ChatSalesAfter)}
            updateRole={updateRole}
          />
          <SubColumn
            title="Direct"
            userPermissions={getSingleRole(userRoles.ChatDirect)}
            updateRole={updateRole}
          />
        </Column>
        <Column category="Reports">
          <SubColumn
            title="View"
            userPermissions={getSingleRole(userRoles.ReportsView)}
            updateRole={updateRole}
          />
        </Column>
        <Column category="Automated messages">
          <SubColumn
            title="View"
            userPermissions={getSingleRole(userRoles.CampaignView)}
            updateRole={updateRole}
          />
          <SubColumn
            title="Manage"
            userPermissions={getSingleRole(userRoles.CampaignManage)}
            updateRole={updateRole}
            handleUpgradeClick={(userId, name, role) =>
              handleGroupLevelChange(
                userId,
                name,
                role,
                userRoleLevels.HasGroupLevelPermission
              )
            }
            handleDowngradeClick={(userId, name, role) =>
              handleGroupLevelChange(userId, name, role, userRoleLevels.HasPermission)
            }
          />
        </Column>
        <Column category="Billing">
          <SubColumn
            title="Manage"
            userPermissions={getSingleRole(userRoles.Billing)}
            updateRole={updateRole}
            handleUpgradeClick={(userId, name, role) =>
              handleGroupLevelChange(
                userId,
                name,
                role,
                userRoleLevels.HasGroupLevelPermission
              )
            }
            handleDowngradeClick={(userId, name, role) =>
              handleGroupLevelChange(userId, name, role, userRoleLevels.HasPermission)
            }
          />
        </Column>
        <Column category="Users">
          <SubColumn
            title="Manage"
            userPermissions={getSingleRole(userRoles.UserManage)}
            updateRole={updateRole}
            handleUpgradeClick={(userId, name, role) =>
              handleGroupLevelChange(
                userId,
                name,
                role,
                userRoleLevels.HasGroupLevelPermission
              )
            }
            handleDowngradeClick={(userId, name, role) =>
              handleGroupLevelChange(userId, name, role, userRoleLevels.HasPermission)
            }
          />
        </Column>
      </div>
      <div className="userPermissions__footer">
        <div className="userPermissions__footer-container">
          {canEditUsers && (
            <Link to="/userPermissions/invoice-controls">
              <button className="userPermissions__helpText">
                <InvoiceIcon
                  style={{
                    width: '30px',
                    height: '30px',
                    fill: '#0079ff'
                  }}
                />{' '}
                Manage who receives invoices
              </button>
            </Link>
          )}
          {!isInternalAdmin && (
            <button
              className="userPermissions__helpText"
              onClick={() => setIsYouTubeVideoVisible(true)}
            >
              <PlayIcon /> Need help? Watch the video tutorial
            </button>
          )}
          <div
            className={`userPermissons__notification ${
              saveSaved ? 'userPermissons__notification--show' : ''
            }`}
          >
            Changes are saved!
          </div>
          {!saveDisabled && (
            <p className="userPermissions__footer-unsaved-changes">
              You have unsaved changes
            </p>
          )}
          <button
            className="userPermissions__save-button"
            onClick={saveRoles}
            disabled={saveDisabled}
          >
            Save changes
          </button>
        </div>
      </div>
    </div>
  )
}

export default UserPermissions
